import type { LotEvent } from '../../types/lot'

export type LotLineageSummary = {
  parents: string[]
  children: string[]
}

export const resolveDisplayedLotLineageSummary = (params: {
  lotId: string
  ledgerLineageSummaryByLotId: Record<string, LotLineageSummary>
}): LotLineageSummary => {
  return params.ledgerLineageSummaryByLotId[params.lotId] ?? { parents: [], children: [] }
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const normalized = String(value ?? '').trim()
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    out.push(normalized)
  })

  return out
}

const isRelationshipEvent = (eventType: LotEvent['eventType']): boolean => (
  eventType === 'split' || eventType === 'merge'
)

export const buildLotLineageSummaryMap = (
  lotIds: string[],
  events: Array<Pick<LotEvent, 'eventType' | 'sourceLotIds' | 'targetLotIds'>>
): Record<string, LotLineageSummary> => {
  const requestedLotIds = uniqueStrings(lotIds)
  const requestedLotIdSet = new Set(requestedLotIds)
  const lotsWithLedgerEvents = new Set<string>()
  const parentSetsByLotId = new Map<string, Set<string>>()
  const childSetsByLotId = new Map<string, Set<string>>()

  const ensureSummaryEntry = (lotId: string): void => {
    if (!requestedLotIdSet.has(lotId)) return
    lotsWithLedgerEvents.add(lotId)
    if (!parentSetsByLotId.has(lotId)) {
      parentSetsByLotId.set(lotId, new Set<string>())
    }
    if (!childSetsByLotId.has(lotId)) {
      childSetsByLotId.set(lotId, new Set<string>())
    }
  }

  events.forEach((event) => {
    const sourceLotIds = uniqueStrings(event.sourceLotIds ?? [])
    const targetLotIds = uniqueStrings(event.targetLotIds ?? [])
    const involvedRequestedLotIds = uniqueStrings([...sourceLotIds, ...targetLotIds])
      .filter((lotId) => requestedLotIdSet.has(lotId))

    if (involvedRequestedLotIds.length === 0) return

    involvedRequestedLotIds.forEach((lotId) => {
      ensureSummaryEntry(lotId)
    })

    if (!isRelationshipEvent(event.eventType)) return

    sourceLotIds.forEach((sourceLotId) => {
      if (!requestedLotIdSet.has(sourceLotId)) return
      const childSet = childSetsByLotId.get(sourceLotId)
      targetLotIds.forEach((targetLotId) => {
        if (!targetLotId || targetLotId === sourceLotId) return
        childSet?.add(targetLotId)
      })
    })

    targetLotIds.forEach((targetLotId) => {
      if (!requestedLotIdSet.has(targetLotId)) return
      const parentSet = parentSetsByLotId.get(targetLotId)
      sourceLotIds.forEach((sourceLotId) => {
        if (!sourceLotId || sourceLotId === targetLotId) return
        parentSet?.add(sourceLotId)
      })
    })
  })

  return Object.fromEntries(
    requestedLotIds
      .filter((lotId) => lotsWithLedgerEvents.has(lotId))
      .map((lotId) => [lotId, {
        parents: Array.from(parentSetsByLotId.get(lotId) ?? []).sort((left, right) => left.localeCompare(right)),
        children: Array.from(childSetsByLotId.get(lotId) ?? []).sort((left, right) => left.localeCompare(right))
      } satisfies LotLineageSummary])
  )
}